import { RoundingCards } from "@swipesense/shared_components_v2/dist/index"
import React, { useState, useEffect, useRef } from "react"
import moment from "moment"
import Loading from "../Loading"
import { Row, Col } from "reactstrap"
import Clock from "../Clock"
import RoundingMonitorLogo from "../Logo/RoundingMonitorLogo"
import PropTypes from "prop-types"
import "./RoundingMonitor.scss"
import {
  evaluateRoundingStatus,
  getResolutionHeight,
} from "../../helpers/sharedHelperFunctions"
import { ExclamationTriangle } from "react-bootstrap-icons"

const RoundingMonitorCard = ({
  unitName,
  facilityName,
  locations,
  loading,
}) => {
  const processLocations = locations => {
    if (!locations) return []
    return locations.map(location => {
      const formattedDurationFromLastRound = lastRoundedTime => {
        if (!lastRoundedTime)
          return (
            <div className="alert-icon">
              <ExclamationTriangle />
            </div>
          )

        const timeSinceRounded = moment.duration(moment().diff(lastRoundedTime))

        const hours = timeSinceRounded.hours()
        const minutes = timeSinceRounded.minutes()

        if (hours === 0) {
          return `${minutes}m ago`
        } else if (minutes === 0) {
          return `${hours}h ago`
        } else {
          return `${hours}h ${minutes}m ago`
        }
      }

      const formattedTimestampFromLastRound = lastRoundedTime => {
        if (!lastRoundedTime) return null
        return new Date(lastRoundedTime).toLocaleString("en-GB", {
          hour: "2-digit",
          minute: "2-digit",
        })
      }

      return {
        ...location,
        durationTimestamp: formattedDurationFromLastRound(location.lastRounded),
        roundingStatus: evaluateRoundingStatus(location),
        roundingTimestamp: formattedTimestampFromLastRound(
          location.lastRounded
        ),
      }
    })
  }

  const sortFunc = (a, b) => {
    if (a.lastRounded > b.lastRounded || !b.lastRounded) return 1
    if (a.lastRounded < b.lastRounded || !a.lastRounded) return -1
    return 0
  }

  const [processedLocations, setProcessedLocations] = useState(
    processLocations(locations)
      .sort(sortFunc)
      .slice(0, 21)
  )
  const locationsRef = useRef(null)

  /* istanbul ignore next */
  useEffect(() => {
    const intervals = setInterval(() => {
      window.location.reload(true)
    }, 3600000)
    return () => clearInterval(intervals)
  }, [])

  useEffect(() => {
    setProcessedLocations(
      processLocations(locations)
        .sort(sortFunc)
        .slice(0, 21)
    )
  }, [locations])

  useEffect(() => {
    clearInterval(locationsRef.current)
    locationsRef.current = setInterval(() => {
      setProcessedLocations(processLocations(processedLocations).sort(sortFunc))
    }, 10000)

    return () => clearInterval(locationsRef.current)
  }, [processedLocations])

  const RoundingCardsWrapper = ({ passedLocations }) => {
    return passedLocations.map(location => {
      return (
        <div
          key={location.id}
          className={`margin-bottom-tv-${getResolutionHeight()} ${getResolutionHeight()}-style`}
        >
          <RoundingCards
            className={location.roundingStatus}
            lastRounded={location.durationTimestamp}
            lastRoundedTime={location.roundingTimestamp}
            roomNumber={location.name}
          />
        </div>
      )
    })
  }

  return (
    <div className="tv-styling content">
      <div className={`rounding-monitor-${getResolutionHeight()}`}>
        <Row>
          <Col>
            <div
              className={`white-text margin-bottom-unit-${getResolutionHeight()}`}
            >
              <div
                className={`tv-heading-1-${getResolutionHeight()} text-align-left bold unit`}
              >
                Unit: {unitName}
              </div>
              <div
                className={`tv-heading-2-${getResolutionHeight()} text-align-left facility margin-bottom-tv-${getResolutionHeight()}`}
              >
                {facilityName}
              </div>
            </div>
          </Col>
          <Col className="text-align-right">
            <RoundingMonitorLogo />
          </Col>
        </Row>
        {loading ? (
          <Loading />
        ) : (
          <>
            <Row>
              <Col xl="3" lg="12" className="display-flex-row">
                <p
                  className={`white-text section-header-${getResolutionHeight()} bold text-align-left`}
                >
                  Room
                </p>
                <p
                  className={`white-text section-header-${getResolutionHeight()} bold text-align-right`}
                >
                  Last Rounded
                </p>
              </Col>
              <Col xl="3" lg="12" className="display-flex-row">
                {locations.length > 7 && (
                  <>
                    <p
                      className={`white-text section-header-${getResolutionHeight()} bold hidden-md text-align-left`}
                    >
                      Room
                    </p>
                    <p
                      className={`white-text section-header-${getResolutionHeight()} bold hidden-md text-align-right`}
                    >
                      Last Rounded
                    </p>
                  </>
                )}
              </Col>
              <Col xl="3" lg="12" className="display-flex-row">
                {locations.length > 14 && (
                  <>
                    <p
                      className={`white-text section-header-${getResolutionHeight()} bold hidden-md text-align-left`}
                    >
                      Room
                    </p>
                    <p
                      className={`white-text section-header-${getResolutionHeight()} bold hidden-md text-align-right`}
                    >
                      Last Rounded
                    </p>
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col xl="3" lg="12">
                <RoundingCardsWrapper
                  passedLocations={processedLocations.slice(0, 7)}
                />
              </Col>
              <Col xl="3" lg="12">
                {locations.length > 7 && (
                  <>
                    <RoundingCardsWrapper
                      passedLocations={processedLocations.slice(7, 14)}
                    />
                  </>
                )}{" "}
              </Col>
              <Col xl="3" lg="12">
                {locations.length > 14 && (
                  <>
                    <RoundingCardsWrapper
                      passedLocations={processedLocations.slice(14, 21)}
                    />
                  </>
                )}
              </Col>
            </Row>
            <Row>
              <Col xl="12">
                {processedLocations.length > 0 &&
                  processedLocations[0].lastRounded == null && (
                    <p
                      className={`alert-icon-disclaimer-${getResolutionHeight()} alert-icon-disclaimer white-text bold time-letter-spacing text-align-left`}
                    >
                      <ExclamationTriangle /> New patient hasn’t been rounded
                      yet, or hardware may be disconnected.
                    </p>
                  )}
                <Clock />
              </Col>
            </Row>
            {/* super secret versioning. Month and day. */}
            <div
              className={`version-number-text-${getResolutionHeight()} white-text light-opacity center-text-mobile text-align-right`}
            >
              v10.13
            </div>
          </>
        )}
      </div>
    </div>
  )
}

RoundingMonitorCard.propTypes = {
  unitName: PropTypes.string.isRequired,
  facilityName: PropTypes.string.isRequired,
  locations: PropTypes.array.isRequired,
  loading: PropTypes.bool,
}

RoundingMonitorCard.defaultProps = {
  unitName: "",
  facilityName: "",
  locations: [],
}

export default RoundingMonitorCard
